<template>
  <div
    class="md:mt-10 mb-2 flex flex-col-reverse md:flex-row gap-10 justify-between md:items-end"
    :class="{ 'md:mb-10': route.name != 'checkout-payment' }"
  >
    <div class="flex-1 md:max-w-2xl animate-appear">
      <BaseButton
        v-if="!(isEmbed() && $route.name == 'checkout-tickets') && $route.name != 'checkout-payment'"
        class="flex gap-1 items-center text-xs text-text hover:underline cursor-pointer"
        size="sm"
      color="default"
        @click="$router.back()"
      >
        <DropdownIcon class="w-3 h-3 fill-current rotate-90" />
        {{ $t('common.back') }}
      </BaseButton>

      <h1 class="h2 mt-2">{{ title }}</h1>

      <div v-if="activeExpire > 0 && route.name == 'checkout-payment'" class="items-center gap-1 font-semibold text-sm hidden md:flex w-full justify-end">
        <StopwatchIcon class="fill-primary w-6 h-6" />
        <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
      </div>

      <slot />

      <CheckoutCodeInput
        v-if="hasDiscountCode && cart.items.length > 0"
        class="md:hidden"
        @on-change="onCodeChange"
        :hasDiscountCode="hasDiscountCode && checkoutStore.event.has_vouchercodes == 1"
      />
    </div>

    <div
      class="md:w-120 md:max-w-full flex -mx-4 md:mx-0 md:justify-end"
      v-if="cart.items.length > 0"
    >
      <button
        type="button"
        @click="toggleVouchercodeInput"
        v-if="hasDiscountCode && checkoutStore.event.has_vouchercodes == 1"
        class="hidden md:flex mr-auto border border-surface-grey px-4 py-1 cursor-pointer rounded-full text-sm font-semibold hover:bg-surface-grey-light transition-colors"
      >
        {{ checkoutStore.event.voucher_code_add_description }}
      </button>
      <button
        type="button"
        @click="toggleGiftcardInput"
        v-if="hasGiftCard"
        class="hidden md:flex mr-auto border border-surface-grey px-4 py-1 cursor-pointer rounded-full text-sm font-semibold hover:bg-surface-grey-light transition-colors"
      >
        {{ 'Indløs gavekort' }}
      </button>

      <div
        ref="countdownRef"
        v-if="activeExpire > 0 && route.name != 'checkout-payment'"
        class="items-center gap-1 font-semibold text-sm hidden md:flex"
      >
        <StopwatchIcon class="fill-primary w-6 h-6" />
        <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
      </div>
    </div>
    <div v-else></div>

  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watchEffect } from 'vue';
  import { useRoute } from 'vue-router';

  import DropdownIcon from '@/components/icons/DropdownIcon.vue';
  import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
  import BaseCountdown from '@/components/ui/BaseCountdown.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import type { CodeItem } from '@/types';
  import { isEmbed } from '@/utils/isEmbed';

  import BaseButton from '../ui/BaseButton.vue';
  import CheckoutCodeInput from './CheckoutCodeInput.vue';

  const props = defineProps<{
    title: string;
    hasDiscountCode?: boolean;
    hasGiftCard?: boolean;
    codeInput?: InstanceType<typeof CheckoutCodeInput> | null;
    onCodeChange?: (codes: CodeItem[]) => void;
  }>();

  const cart = useCartStore();
  const checkoutStore = useCheckoutStore();
  const route = useRoute();

  const emit = defineEmits<{ 'update:countdownFixed': [value: boolean] }>();

  const countdownRef = ref<HTMLElement | null>(null);
  const countdownFixed = ref(false);

  watchEffect((onCleanup) => {
    const el = countdownRef.value;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        countdownFixed.value = !entry.isIntersecting;
        emit('update:countdownFixed', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-84px 0px 0px 0px' }
    );
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  });

  const activeExpire = computed(() =>
    route.name === 'checkout-payment' ? cart.paymentFailedExpire : cart.cartExpire
  );

  function toggleVouchercodeInput() {
    if (props.codeInput) {
      props.codeInput.toggleVouchercodeInput();
    }
  }

  function toggleGiftcardInput() {
    if (props.codeInput) {
      props.codeInput.toggleGiftcardInput();
    }
  }
</script>
